
export const LIMITS = {
  usernameMin: 2,
  usernameMax: 32,
  displayNameMax: 32,
  passwordMin: 8,
  passwordMax: 128,
  bioMax: 190,
  customStatusMax: 128,
  serverNameMin: 2,
  serverNameMax: 100,
  serverDescriptionMax: 500,
  channelNameMax: 100,
  channelTopicMax: 1024,
  roleNameMax: 32,
  messageMax: 4000,
  autoModWordsMax: 200,
  eventNameMax: 100,
  serverRulesMax: 2000,
  forumTagsMax: 20,
  forumTagNameMax: 20,
  forumPostTagsMax: 5,
  eventDescriptionMax: 1000,
  eventLocationMax: 100,
  attachmentsPerMessage: 5,
  /** An attachment's description (alt text) for screen readers. */
  attachmentDescriptionMax: 500,
  /** Voice messages: the longest recording. */
  voiceMessageMaxMs: 5 * 60_000,
  /** Saved messages per account. */
  savedMessagesMax: 500,
  /** Earlier versions kept per edited message. */
  messageVersionsMax: 20,
  messagesPageSize: 50,
  maxMessagesPageSize: 100,
  searchPageSize: 25,
  searchQueryMax: 200,
  maxRolesPerServer: 50,
  maxChannelsPerServer: 200,
  maxServersPerUser: 100,
  maxPinsPerChannel: 50,
  maxEmojisPerServer: 50,
  emojiMaxBytes: 512 * 1024,
  pollQuestionMax: 300,
  pollOptionMax: 55,
  pollOptionsMin: 2,
  pollOptionsMax: 10,
  /** Group DMs: people in one, the owner included. */
  groupDmMaxMembers: 10,
  groupNameMax: 100,
  threadNameMax: 100,
  customCssMax: 50_000,
  savedLayoutsMax: 10,
  layoutNameMax: 32,
} as const;

/** The layout everyone starts with. */
export const DEFAULT_LAYOUT = {
  serverList: 'LEFT',
  channelList: 'LEFT',
  memberList: 'SHOWN',
  avatarSize: 'MEDIUM',
  spacing: 'NORMAL',
  chatWidth: 'FULL',
} as const;

/** The colours you choose for a custom theme; the app works out the rest from these. */
export const CUSTOM_THEME_COLORS = ['background', 'sidebar', 'rail', 'panel', 'card', 'text', 'accent', 'secondary'] as const;

/** Slow mode choices, in seconds (0 = off). */
export const SLOW_MODE_OPTIONS = [0, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 21600] as const;

/** How long a member can be timed out for, in seconds. */
export const TIMEOUT_DURATIONS = [
  { seconds: 60, label: '60 seconds' },
  { seconds: 300, label: '5 minutes' },
  { seconds: 600, label: '10 minutes' },
  { seconds: 3600, label: '1 hour' },
  { seconds: 86_400, label: '1 day' },
  { seconds: 604_800, label: '1 week' },
] as const;

/** What AutoMod can do to whoever breaks a rule, besides blocking the message. */
export const AUTOMOD_TIMEOUTS = [
  { seconds: 0, label: 'Only block the message' },
  { seconds: 60, label: 'Time out for 60 seconds' },
  { seconds: 300, label: 'Time out for 5 minutes' },
  { seconds: 600, label: 'Time out for 10 minutes' },
  { seconds: 3600, label: 'Time out for 1 hour' },
  { seconds: 86_400, label: 'Time out for 1 day' },
] as const;

/** Spam AutoMod blocks: more than this many messages in the window, or the same message repeated. */
export const AUTOMOD_SPAM = { maxMessages: 5, windowSeconds: 5, maxRepeats: 3, repeatWindowSeconds: 30 } as const;

/** How long a poll can stay open, in hours. */
export const POLL_DURATIONS_HOURS = [1, 4, 8, 24, 72, 168] as const;

/** How long a client should wait before considering a "typing" signal stale. */
export const TYPING_TIMEOUT_MS = 6000;
/** Minimum interval between typing signals emitted by a client. */
export const TYPING_THROTTLE_MS = 3000;
/** Inactivity before a client reports itself as idle. */
export const IDLE_AFTER_MS = 5 * 60 * 1000;

/** Allowed invite expiry options in seconds (`null` = never). */
export const INVITE_EXPIRY_OPTIONS = [1800, 3600, 21600, 43200, 86400, 604800] as const;
